import js_sha256 from 'js-sha256';
import {
  isArt,
  isNames,
  isNumbers,
  isText,
  isUpload
} from '~/data/accessors/icon';
import {sanitizeUrl} from '~/utils/url';

export const getSearchTerm = path => {
  if (!path?.startsWith('searchArt/')) return null;
  const term = path.split('/')[1];
  return term || null;
};

const queryIdBySubpath = regex => path => {
  if (!path) return null;
  const subpath = path.match(regex)?.[0];
  if (!subpath) return null;
  const categoryId = subpath.split('/')[1];
  return categoryId;
};

/**
 * "addArt/cat/4/sub/2320" => "4"
 */
export const queryCategoryIdByPath = queryIdBySubpath(/cat\/\d+/g);

/**
 * "addArt/cat/4/sub/2320" => "2320"
 */
export const querySubcategoryIdByPath = queryIdBySubpath(/sub\/\d+/g);

export const queryCategoryNameById = (id, categoryNames = {}) => {
  return categoryNames[id]?.replace('/', '&') || null;
};

export const translateCategoryPath = (path, categoryNames) => {
  const category = queryCategoryNameById(
    queryCategoryIdByPath(path),
    categoryNames
  );
  const subcategory = queryCategoryNameById(
    querySubcategoryIdByPath(path),
    categoryNames
  );
  if (category && subcategory) return `${category}/${subcategory}`;
  return category || subcategory || null;
};

export const translateBrowsedCategories = (
  paths,
  categoryNames,
  categoryPath
) => {
  if (categoryPath?.startsWith('searchArt/')) return null;
  return paths
    .map(path => {
      const id = queryCategoryIdByPath(path) || querySubcategoryIdByPath(path);
      return queryCategoryNameById(id, categoryNames);
    })
    .join('/');
};

export const queryMostSpecificId = path => {
  const subcategoryId = querySubcategoryIdByPath(path);
  const categoryId = queryCategoryIdByPath(path);
  return subcategoryId || categoryId || null;
};

export const createCanvasRemoveEvents = (
  {iconIds, iconTypes},
  {getById, categoryNames, targetIconType}
) => {
  if (!iconIds || !iconTypes) return null;

  const deletedIcons = [];

  iconIds.forEach((id, iconIndex) => {
    const iconType = iconTypes[iconIndex];

    if (isArt(targetIconType) && isArt(iconType)) {
      const {clipartId, categoryPath} = getById(id);
      deletedIcons.push({
        event: 'art_remove',
        type: iconTypes[iconIndex],
        art_id: clipartId,
        art_category_path: translateCategoryPath(categoryPath, categoryNames),
        art_search_term: getSearchTerm(categoryPath)
      });
    } else if (isUpload(targetIconType) && isUpload(iconType)) {
      deletedIcons.push({
        event: 'lab_upload_removed'
      });
    } else if (isText(targetIconType) && isText(iconType)) {
      deletedIcons.push({
        event: 'text_removed'
      });
    } else if (isNamesOrNumbersIconType(targetIconType) && isNames(iconType)) {
      deletedIcons.push({
        event: 'lab_edit_tools',
        tool_icon_type: 'namesandnumbers',
        tool_name: 'names_enabled',
        tool_value: 'off',
        tool_source: 'panel'
      });
    } else if (
      isNamesOrNumbersIconType(targetIconType) &&
      isNumbers(iconType)
    ) {
      deletedIcons.push({
        event: 'lab_edit_tools',
        tool_icon_type: 'namesandnumbers',
        tool_name: 'numbers_enabled',
        tool_value: 'off',
        tool_source: 'panel'
      });
    }
  });

  return deletedIcons;
};

const isNamesOrNumbersIconType = iconTypeDescription => {
  return iconTypeDescription === 'NAMES_NUMBERS';
};

export const hashFunc = email => {
  const sha256 = js_sha256.sha256;
  return email ? sha256(email) : '';
};

export const getTotalQuantity = quantities => {
  return (
    quantities &&
    Object.values(quantities)
      .map(obj => Object.values(obj).reduce((a, b) => a + b, 0))
      .reduce((a, b) => a + b, 0)
  );
};

export const addEvents = (existingEvents, newEvents) => {
  if (!existingEvents || !newEvents) return null;

  const addEventEnsureValueIsArray = (allEvents, events, eventKey) => {
    const newEvent = events[eventKey];

    if (Array.isArray(allEvents[eventKey])) {
      allEvents[eventKey].push(newEvent);
    } else {
      allEvents[eventKey] = Array.isArray(newEvent) ? newEvent : [newEvent];
    }

    return allEvents;
  };

  let allEvents = Object.keys(existingEvents).reduce((allEvents, eventKey) => {
    return addEventEnsureValueIsArray(allEvents, existingEvents, eventKey);
  }, {});

  allEvents = Object.keys(newEvents).reduce((allEvents, newEventKey) => {
    return addEventEnsureValueIsArray(allEvents, newEvents, newEventKey);
  }, allEvents);

  return allEvents;
};

export const getAnalyticsEventsDictionary = eventsList => {
  return eventsList.reduce((acc, event) => {
    return addEvents(acc, event);
  }, {});
};

export const getStylePrimaryColorFromNextColors = (style, nextColorIds) => {
  if (
    !style?.colors ||
    !nextColorIds ||
    !Array.isArray(Object.values(nextColorIds)) ||
    Object.values(nextColorIds).length <= 0
  ) {
    return null;
  }
  const colorId = Object.values(nextColorIds)[0];
  const color = style.colors.find(color => color?.id === colorId);
  return `${color?.id}_${color?.name}`;
};

export const getStylePrimaryCategoryDescription = style => {
  if (!style?.primary_category_id) return null;
  return style?.primary_category_name
    ? `${style.primary_category_id}_${style.primary_category_name}`
    : style.primary_category_id;
};

export const isTextIcon = ({getIconById, state, iconId}) => {
  const selectedIcon = getIconById(state)(iconId);
  return isText(selectedIcon?.type);
};

export const hasTextIcon = ({transforms, iconIds, getIconById, state}) => {
  if (transforms) {
    return transforms?.some?.(transform => isText(transform?.icon?.type));
  }
  return iconIds?.some?.(iconId => {
    return isTextIcon({getIconById, state, iconId});
  });
};

export const isUploadIcon = ({getIconById, state, iconId}) => {
  const selectedIcon = getIconById(state)(iconId);
  return isUpload(selectedIcon?.type);
};

export const hasUploadIcon = ({transforms, iconIds, getIconById, state}) => {
  if (transforms) {
    return transforms?.some?.(transform => isUpload(transform?.icon?.type));
  }
  return iconIds?.some?.(iconId => {
    return isUploadIcon({getIconById, state, iconId});
  });
};

export const isArtIcon = ({getIconById, state, iconId}) => {
  const selectedIcon = getIconById(state)(iconId);
  return isArt(selectedIcon?.type);
};

export const hasArtIcon = ({transforms, iconIds, getIconById, state}) => {
  if (transforms) {
    return transforms?.some?.(transform => isArt(transform?.icon?.type));
  }
  return iconIds?.some?.(iconId => {
    return isArtIcon({getIconById, state, iconId});
  });
};

export const getLabShareEvent = shareType => {
  if (!shareType) return null;
  return {
    event: 'lab_share',
    share_type: shareType
  };
};

export const getLabZoomEvent = zoomDirection => {
  if (!zoomDirection) return null;
  return {
    event: 'lab_zoom',
    tool_value: zoomDirection
  };
};

export const getLabQuoteEvent = ({quotePath, eventName}) => {
  if (!eventName || !quotePath) return null;
  return {
    event: eventName,
    quote_path: quotePath
  };
};

export const getLabContactUsEvent = contactType => {
  if (!contactType) return null;
  return {
    event: 'lab_contact_us',
    contact_type: contactType
  };
};

const WTOOptionValues = {
  SHIPPING: {
    ORGANIZER: 'single',
    COMMUNITY: 'multiple'
  },
  ORGANIZER: 'single',
  COMMUNITY: 'group'
};

export const getWTOShippingOption = project =>
  WTOOptionValues.SHIPPING[project?.shipping];
export const getWTOSizesOption = project => WTOOptionValues[project?.sizes];
export const getWTOPaymentOption = project => WTOOptionValues[project?.payment];

export const getSelectedWTOOptions = project => ({
  shipping: getWTOShippingOption(project),
  sizes: getWTOSizesOption(project),
  payment: getWTOPaymentOption(project)
});

export const getWTOOptionsEvent = ({project, eventName}) => {
  const {shipping, sizes, payment} = getSelectedWTOOptions(project);
  if (!shipping || !payment || !sizes) return null;
  return {
    event: eventName,
    oo_shipping: shipping,
    oo_sizes: sizes,
    oo_payment: payment
  };
};

export const getCategoriesFromPath = categoryPath => {
  if (!categoryPath) return null;
  const pathParts = categoryPath.split('/');
  return {
    rootCategory: sanitizeUrl('All Products'),
    parentCategory: pathParts.length > 0 ? pathParts[1] : null,
    primaryCategory: pathParts.length > 1 ? pathParts[2] : null
  };
};

export const boolToNumber = bool => (bool ? 1 : 0);
